/**
 * SouthIndianTradition — Tamil/Telugu/Kannada wedding.
 *
 * A traditional South Indian ceremony with temple aesthetics,
 * animated diyas, and cultural authenticity.
 */

import React from 'react';
import { registry } from '../registry';
import type { InvitationData, TemplateMetadata } from '../types';
import { resolveContent } from './content';
import { southIndianTheme, themeCssVars } from './theme';
import { HeroSection } from './sections/HeroSection';
import { ProgrammeSection } from './sections/ProgrammeSection';
import { RsvpSection } from './sections/RsvpSection';

const META: TemplateMetadata = {
  id: 'south-indian-tradition-experience',
  name: 'South Indian Tradition — Tamil/Telugu/Kannada',
  occasionType: 'WEDDING',
  description:
    'A traditional South Indian wedding: temple gopuram hero, animated diyas, ceremony programme with tilak, mangala sutra, and reception.',
  isPremium: true,
  tags: ['south-indian', 'tamil', 'telugu', 'kannada', 'temple', 'traditional', 'animated'],
  colorScheme: {
    primary: southIndianTheme.maroon,
    accent: southIndianTheme.mustard,
    bg: southIndianTheme.ivory,
  },
};

export function SouthIndianTraditionTemplate({ data }: { data: InvitationData }) {
  const content = React.useMemo(() => resolveContent(data), [data]);
  const invitationId = data.invitationId ?? data.id ?? null;
  const isLive = data.status === 'PUBLISHED' || data.status === 'PROCESSING';

  const scrollToRsvp = React.useCallback(() => {
    document.getElementById('rsvp')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }, []);

  return (
    <main
      className="si-root min-h-screen scroll-smooth bg-[var(--si-ivory)] font-sans text-[var(--si-ink)] antialiased"
      style={themeCssVars(southIndianTheme)}
    >
      <HeroSection content={content.hero} onRsvp={scrollToRsvp} />
      <ProgrammeSection content={content.ceremony} />
      <RsvpSection content={content.rsvp} invitationId={invitationId} isLive={isLive} />
    </main>
  );
}

registry.register({ metadata: META, component: SouthIndianTraditionTemplate });
